import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Edit, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { API, formatApiError, useAuth } from "@/lib/shared";
import { CheckoutModal } from "@/components/DaySchedule";

export const EMPTY_VISIT = {
  patient_id: '', appointment_id: '', visit_date: new Date().toISOString().slice(0, 10),
  service_type_id: '', staff_id: '',
  blood_pressure: '', heart_rate: '', temperature: '', oxygen_saturation: '', weight: '',
  iv_solution: '', additives: '', infusion_rate: '', volume_administered: '', iv_site: '', gauge: '',
  chief_complaint: '', assessment: '', treatment_notes: '', patient_tolerance: '', adverse_reactions: '',
  post_treatment_instructions: '', follow_up_needed: false, follow_up_date: '',
};

export const VisitManagement = ({ patients, staff, serviceTypes, appointments = [], onAppointmentsRefresh, autoOpen, onAutoOpenConsumed }) => {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_VISIT);
  const [loading, setLoading] = useState(false);
  const [checkoutAppt, setCheckoutAppt] = useState(null);

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API}/visits`); setVisits(res.data); }
    catch (e) { toast.error('Failed to load visits'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = useCallback(() => { setEditing(null); setFormData(EMPTY_VISIT); setModalOpen(true); }, []);
  const openEdit = (v) => { setEditing(v); setFormData({ ...EMPTY_VISIT, ...v }); setModalOpen(true); };

  useEffect(() => {
    if (autoOpen === 'new') { openCreate(); onAutoOpenConsumed?.(); }
  }, [autoOpen, onAutoOpenConsumed, openCreate]);

  // Find the best appointment to link this visit to (for auto check-in/check-out on Complete Visit)
  const findLinkedAppointment = useCallback((data) => {
    if (!data?.patient_id) return null;
    // 1) Explicit appointment_id on the visit
    if (data.appointment_id) {
      const explicit = appointments.find(a => a.id === data.appointment_id);
      if (explicit) return explicit;
    }
    // 2) Any non-completed appointment for this patient on the same visit_date
    const sameDay = appointments.filter(a =>
      a.patient_id === data.patient_id &&
      a.status !== 'completed' && a.status !== 'cancelled' &&
      (a.start_datetime || '').slice(0, 10) === data.visit_date
    );
    if (sameDay.length) return sameDay[0];
    // 3) Fallback: earliest non-completed appointment for this patient
    const upcoming = appointments
      .filter(a => a.patient_id === data.patient_id && a.status !== 'completed' && a.status !== 'cancelled')
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
    return upcoming[0] || null;
  }, [appointments]);

  const persistVisit = async () => {
    if (!formData.patient_id) { toast.error('Select a patient'); return null; }
    const payload = { ...formData };
    ['heart_rate', 'oxygen_saturation'].forEach(k => { payload[k] = payload[k] ? parseInt(payload[k]) : null; });
    ['temperature', 'weight'].forEach(k => { payload[k] = payload[k] ? parseFloat(payload[k]) : null; });
    if (editing) {
      await axios.put(`${API}/visits/${editing.id}`, payload);
      toast.success('Visit updated');
    } else {
      await axios.post(`${API}/visits`, payload);
      toast.success('Visit documented');
    }
    return payload;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const saved = await persistVisit();
      if (!saved) return;
      setModalOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleCompleteVisit = async () => {
    setLoading(true);
    try {
      const saved = await persistVisit();
      if (!saved) return;

      // Find the appointment to auto check-out
      const linkedApt = findLinkedAppointment(saved);
      if (!linkedApt) {
        toast.info('Visit saved. No matching appointment found to check out.');
        setModalOpen(false);
        load();
        return;
      }

      // Auto check-in if the appointment isn't already checked in
      if (!linkedApt.checked_in_at) {
        try {
          await axios.post(`${API}/appointments/${linkedApt.id}/check-in`);
        } catch (e) {
          // If the backend says "already checked in" it's fine to proceed
          const detail = e.response?.data?.detail || '';
          if (!detail.toLowerCase().includes('already checked in')) {
            toast.error(formatApiError(detail) || 'Could not auto check-in');
          }
        }
      }

      // Refresh appointment data so CheckoutModal shows the latest state
      onAppointmentsRefresh && onAppointmentsRefresh();
      setModalOpen(false);
      load();
      // Auto-open checkout modal so the $$ step isn't skipped
      setCheckoutAppt({ ...linkedApt, checked_in_at: linkedApt.checked_in_at || new Date().toISOString() });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this visit record?')) return;
    try { await axios.delete(`${API}/visits/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const patientName = (pid) => { const p = patients.find(x => x.id === pid); return p ? `${p.first_name} ${p.last_name}` : 'Unknown'; };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Visit Documentation</h2>
        <Button size="sm" onClick={openCreate} data-testid="add-visit-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />Document Visit</Button>
      </div>

      <div className="space-y-2 max-h-[500px] overflow-y-auto">
        {visits.map(v => (
          <div key={v.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div>
              <p className="font-medium">{patientName(v.patient_id)}</p>
              <p className="text-xs text-muted-foreground">{v.visit_date} · {v.iv_solution || 'No solution recorded'} {v.adverse_reactions ? '· ⚠ Adverse reaction noted' : ''}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => openEdit(v)}><Edit className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(v.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {visits.length === 0 && <p className="text-center text-muted-foreground py-8">No visits documented yet. Click "Document Visit" to record an IV therapy session.</p>}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Visit Record' : 'Document IV Therapy Session'}</DialogTitle>
            <DialogDescription>Complete clinical documentation for this session</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Session */}
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Session</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Patient *</Label>
                  <Select value={formData.patient_id} onValueChange={(v) => update('patient_id', v)}>
                    <SelectTrigger data-testid="visit-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                    <SelectContent>{patients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Visit Date</Label><Input type="date" value={formData.visit_date} onChange={(e) => update('visit_date', e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1">
                  <Label>Service Type</Label>
                  <Select value={formData.service_type_id} onValueChange={(v) => update('service_type_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Select service" /></SelectTrigger>
                    <SelectContent>{serviceTypes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Administering Staff</Label>
                  <Select value={formData.staff_id} onValueChange={(v) => update('staff_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Select staff" /></SelectTrigger>
                    <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Vitals */}
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Vitals</p>
              <div className="grid grid-cols-5 gap-2">
                <div className="space-y-1"><Label className="text-xs">BP</Label><Input value={formData.blood_pressure} onChange={(e) => update('blood_pressure', e.target.value)} placeholder="120/80" /></div>
                <div className="space-y-1"><Label className="text-xs">HR</Label><Input type="number" value={formData.heart_rate} onChange={(e) => update('heart_rate', e.target.value)} placeholder="72" /></div>
                <div className="space-y-1"><Label className="text-xs">Temp (°F)</Label><Input type="number" step="0.1" value={formData.temperature} onChange={(e) => update('temperature', e.target.value)} placeholder="98.6" /></div>
                <div className="space-y-1"><Label className="text-xs">SpO₂</Label><Input type="number" value={formData.oxygen_saturation} onChange={(e) => update('oxygen_saturation', e.target.value)} placeholder="98" /></div>
                <div className="space-y-1"><Label className="text-xs">Weight (lb)</Label><Input type="number" step="0.1" value={formData.weight} onChange={(e) => update('weight', e.target.value)} /></div>
              </div>
            </div>

            {/* Treatment details */}
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Treatment Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>IV Solution</Label><Input value={formData.iv_solution} onChange={(e) => update('iv_solution', e.target.value)} placeholder="e.g., 1L NS" /></div>
                <div className="space-y-1"><Label>Additives</Label><Input value={formData.additives} onChange={(e) => update('additives', e.target.value)} placeholder="B-Complex 1mL, Mg 1g..." /></div>
                <div className="space-y-1"><Label>Infusion Rate</Label><Input value={formData.infusion_rate} onChange={(e) => update('infusion_rate', e.target.value)} placeholder="250 mL/hr" /></div>
                <div className="space-y-1"><Label>Volume Administered</Label><Input value={formData.volume_administered} onChange={(e) => update('volume_administered', e.target.value)} placeholder="1000 mL" /></div>
                <div className="space-y-1"><Label>IV Site</Label><Input value={formData.iv_site} onChange={(e) => update('iv_site', e.target.value)} placeholder="Left antecubital" /></div>
                <div className="space-y-1"><Label>Catheter Gauge</Label><Input value={formData.gauge} onChange={(e) => update('gauge', e.target.value)} placeholder="22g" /></div>
              </div>
            </div>

            {/* Clinical notes */}
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Clinical Notes</p>
              <div className="space-y-3">
                <div className="space-y-1"><Label>Chief Complaint / Reason for Visit</Label><Textarea rows={2} value={formData.chief_complaint} onChange={(e) => update('chief_complaint', e.target.value)} /></div>
                <div className="space-y-1"><Label>Assessment</Label><Textarea rows={2} value={formData.assessment} onChange={(e) => update('assessment', e.target.value)} /></div>
                <div className="space-y-1"><Label>Treatment Notes</Label><Textarea rows={3} value={formData.treatment_notes} onChange={(e) => update('treatment_notes', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Patient Tolerance</Label><Input value={formData.patient_tolerance} onChange={(e) => update('patient_tolerance', e.target.value)} placeholder="Well tolerated, no complaints" /></div>
                  <div className="space-y-1"><Label className="text-destructive">Adverse Reactions</Label><Input value={formData.adverse_reactions} onChange={(e) => update('adverse_reactions', e.target.value)} placeholder="None, or describe" /></div>
                </div>
                <div className="space-y-1"><Label>Post-Treatment Instructions</Label><Textarea rows={2} value={formData.post_treatment_instructions} onChange={(e) => update('post_treatment_instructions', e.target.value)} /></div>
              </div>
            </div>

            {/* Follow-up */}
            <div className="bg-muted/30 p-3 rounded-lg border border-border/50">
              <div className="flex items-center gap-3 mb-2">
                <Switch checked={!!formData.follow_up_needed} onCheckedChange={(v) => update('follow_up_needed', v)} />
                <Label className="font-semibold">Schedule Follow-Up Call</Label>
              </div>
              {formData.follow_up_needed && (
                <Input type="date" value={formData.follow_up_date} onChange={(e) => update('follow_up_date', e.target.value)} />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={loading}
              data-testid="save-visit-btn"
              className="border-primary/40 text-primary hover:bg-primary/10"
            >
              {loading ? 'Saving…' : (editing ? 'Update' : 'Save Visit')}
            </Button>
            {user?.role !== 'management' && (
              <Button
                onClick={handleCompleteVisit}
                disabled={loading}
                data-testid="complete-visit-btn"
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-black font-semibold hover:from-emerald-400 hover:to-emerald-500"
                title="Save the visit note, mark the appointment checked-out, and open payment"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />{loading ? 'Completing…' : 'Save & Complete Visit'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {checkoutAppt && (
        <CheckoutModal
          isOpen={!!checkoutAppt}
          onClose={() => setCheckoutAppt(null)}
          appointment={checkoutAppt}
          patient={patients.find(p => p.id === checkoutAppt.patient_id)}
          serviceTypes={serviceTypes}
          onComplete={() => {
            setCheckoutAppt(null);
            onAppointmentsRefresh && onAppointmentsRefresh();
            toast.success('Visit checked out ✓');
          }}
        />
      )}
    </div>
  );
};

// ==================== FOLLOW-UP CALLS ====================
